import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useProject } from "@/components/AppShell";
import { DownloadMdButton } from "@/components/DownloadMdButton";
import { buildAuditMd } from "@/lib/mdBuilders";
import { toast } from "sonner";
import { RefreshCw, AlertOctagon, AlertTriangle, Info, FileText, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { CreditActionButton } from "@/components/CreditActionButton";
import { MetricInfo } from "@/components/MetricInfo";
import { DownloadCsvButton } from "@/components/DownloadCsvButton";

const LEVEL = {
    error: { icon: AlertOctagon, color: "text-destructive border-destructive/40" },
    warning: { icon: AlertTriangle, color: "text-warning border-warning/40" },
    notice: { icon: Info, color: "text-secondary border-secondary/40" },
};

const DEPTH_OPTIONS = [
    { value: 20, label: "Quick · 20 pages" },
    { value: 50, label: "Standard · 50 pages" },
    { value: 100, label: "Thorough · 100 pages" },
];

export default function SiteAudit() {
    const project = useProject();
    const [audit, setAudit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [discover, setDiscover] = useState(null);
    const [maxPages, setMaxPages] = useState(20);
    const [expandedId, setExpandedId] = useState(null);

    const load = () => {
        if (!project) return;
        setLoading(true);
        client.get(`/projects/${project.id}/audit/latest`)
            .then((r) => setAudit(r.data))
            .catch(() => setAudit(null))
            .finally(() => setLoading(false));
    };
    useEffect(load, [project]);

    useEffect(() => {
        if (!project) return;
        setDiscover(null);
        client.post(`/projects/${project.id}/audit/discover`)
            .then((r) => {
                setDiscover(r.data);
                if (r.data?.page_count) setMaxPages(Math.min(r.data.page_count, 50));
            })
            .catch(() => setDiscover({ page_count: null, source: "unknown" }));
    }, [project]);

    const rerun = async () => {
        try {
            const r = await client.post(`/projects/${project.id}/audit`, { max_pages: maxPages });
            setAudit(r.data);
            setExpandedId(null);
            toast.success(`Audit complete — crawled ${r.data.pages_crawled} real page(s) of ${project.domain}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Audit failed");
        }
    };

    const ScopeSelector = () => (
        <div className="flex items-center gap-2 flex-wrap" data-testid="audit-scope-selector">
            <label className="text-xs text-muted-foreground">Crawl depth</label>
            <select
                data-testid="audit-max-pages-select"
                value={maxPages}
                onChange={(e) => setMaxPages(parseInt(e.target.value, 10))}
                className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
            >
                {DEPTH_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
                {discover?.page_count > 0 && (
                    <option value={Math.min(discover.page_count, 200)}>
                        All discovered · {Math.min(discover.page_count, 200)} pages
                    </option>
                )}
            </select>
            <span className="text-[10px] font-mono text-muted-foreground/70" data-testid="audit-discover-hint">
                {discover === null
                    ? "Checking site size…"
                    : discover.page_count
                        ? `Found ~${discover.page_count} pages via sitemap.xml`
                        : "No sitemap found — pick a depth manually"}
            </span>
        </div>
    );

    if (!project || loading) {
        return <div className="text-muted-foreground font-mono text-sm">loading audit…</div>;
    }

    // Never-audited state — the whole page is a big "Run first audit" CTA
    if (!audit) {
        return (
            <div className="space-y-8" data-testid="audit-page-empty">
                <div className="overline">Technical SEO</div>
                <h1 className="font-display text-3xl md:text-4xl tracking-tight">Site Audit</h1>
                <div className="border border-border bg-card/40 p-8 max-w-2xl space-y-5">
                    <p className="text-sm text-muted-foreground">
                        No audit has been run for <span className="font-mono text-foreground">{project.domain}</span> yet.
                        Kicking off an audit runs a real crawl of this exact domain via DataForSEO, scores real Core Web Vitals via Lighthouse, and flags the real technical issues we find — no other site's data is ever mixed in.
                    </p>
                    <ScopeSelector />
                    <CreditActionButton
                        actionKey="audit"
                        testid="run-first-audit-btn"
                        onConfirm={rerun}
                        description={`We'll run a real crawl of ${project.domain} (up to ${maxPages} pages) plus a live Lighthouse pass. This is a real DataForSEO spend and can take 30-90 seconds — re-crawls are always manual, never automatic.`}
                    >
                        Run first audit
                    </CreditActionButton>
                </div>
            </div>
        );
    }
    const cwv = audit.core_web_vitals;

    return (
        <div className="space-y-8" data-testid="audit-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Technical SEO</div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Site Audit</h1>
                    <p className="text-sm text-muted-foreground mt-2">
                        Last run: {new Date(audit.run_at).toLocaleString()} · {audit.pages_crawled.toLocaleString()} real pages crawled on {project.domain}
                    </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <ScopeSelector />
                    <div className="flex items-center gap-2 flex-wrap">
                        <CreditActionButton
                            actionKey="audit"
                            testid="rerun-audit-btn"
                            onConfirm={rerun}
                            description={`Re-runs a real crawl on ${project.domain} (up to ${maxPages} pages) — checks Core Web Vitals via Lighthouse and refreshes the issues list. Can take 30-90 seconds. Re-crawls are manual only.`}
                        >
                            <span className="inline-flex items-center gap-1">
                                <RefreshCw className="w-4 h-4" /> Re-run audit
                            </span>
                        </CreditActionButton>
                        <Link data-testid="view-report-btn" to="/app/audit/report"
                              className="border border-border px-4 py-2 text-sm hover:border-primary flex items-center gap-2">
                            <FileText className="w-4 h-4" /> View printable report
                        </Link>
                        <DownloadMdButton
                            filenamePrefix={`inphase-audit-${project?.domain || "project"}`}
                            buildMarkdown={() => buildAuditMd({ project, audit })}
                            testId="download-audit-md"
                        />
                        <DownloadCsvButton
                            filenamePrefix={`inphase-audit-issues-${project?.domain || "project"}`}
                            testId="download-audit-issues-csv"
                            label="Export issues CSV"
                            buildRows={() => audit.issues.flatMap((iss) => {
                                const urls = iss.urls && iss.urls.length ? iss.urls : ["(site-wide — no single-URL breakdown)"];
                                return urls.map((u) => ({
                                    Issue: iss.title,
                                    Severity: iss.severity,
                                    Level: iss.level,
                                    Count: iss.count,
                                    Why_It_Matters: iss.why || "",
                                    How_To_Fix: iss.fix,
                                    Affected_URL: u,
                                }));
                            })}
                        />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div className="border border-border bg-card/40 p-8 lg:col-span-1" data-testid="health-score">
                    <div className="overline mb-4 flex items-center gap-1.5">Health Score <MetricInfo metricKey="health_score" /></div>
                    <div className="relative w-40 h-40 mx-auto">
                        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                            <circle cx="50" cy="50" r="42" fill="none" stroke="#1a1a1a" strokeWidth="6" />
                            <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(196 100% 61%)" strokeWidth="6"
                                    strokeDasharray={`${(audit.health_score / 100) * 264} 264`} strokeLinecap="butt" />
                        </svg>
                        <div className="absolute inset-0 grid place-items-center">
                            <div>
                                <div className="font-mono text-4xl text-center">{audit.health_score}</div>
                                <div className="text-xs text-muted-foreground text-center mt-1">/ 100</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="border border-border bg-card/40 p-6">
                    <div className="overline flex items-center gap-1.5">Errors <MetricInfo metricKey="audit_errors" /></div>
                    <div className="font-mono text-4xl text-destructive mt-3">{audit.errors}</div>
                </div>
                <div className="border border-border bg-card/40 p-6">
                    <div className="overline flex items-center gap-1.5">Warnings <MetricInfo metricKey="audit_warnings" /></div>
                    <div className="font-mono text-4xl text-warning mt-3">{audit.warnings}</div>
                </div>
                <div className="border border-border bg-card/40 p-6">
                    <div className="overline flex items-center gap-1.5">Notices <MetricInfo metricKey="audit_notices" /></div>
                    <div className="font-mono text-4xl text-secondary mt-3">{audit.notices}</div>
                </div>
            </div>

            {/* CWV */}
            <div className="border border-border bg-card/40 p-6">
                <div className="overline mb-4">Core Web Vitals</div>
                {cwv?.available === false && (
                    <p className="text-xs text-warning mb-4" data-testid="cwv-unavailable">
                        Live Lighthouse pass didn't return data for this domain — Core Web Vitals below may be incomplete.
                    </p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                    {[
                        ["LCP", cwv.lcp, "s", cwv.lcp <= 2.5, "lcp"],
                        ["CLS", cwv.cls, "", cwv.cls <= 0.1, "cls"],
                        ["INP", cwv.inp, "ms", cwv.inp <= 200, "inp"],
                        ["Mobile", cwv.mobile_score, "/100", cwv.mobile_score >= 90, "mobile_pagespeed"],
                        ["Desktop", cwv.desktop_score, "/100", cwv.desktop_score >= 90, "desktop_pagespeed"],
                    ].map(([l, v, u, good, mk]) => (
                        <div key={l}>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">{l} <MetricInfo metricKey={mk} /></div>
                            <div className={`font-mono text-2xl mt-1 ${good ? "text-success" : "text-warning"}`}>{v}<span className="text-xs text-muted-foreground ml-1">{u}</span></div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Issues */}
            <div className="border border-border bg-card/40" data-testid="issues-table">
                <div className="p-6 border-b border-border">
                    <div className="overline">Issues</div>
                    <div className="text-sm text-muted-foreground mt-1">Sorted by impact</div>
                </div>
                <div className="divide-y divide-border/50">
                    {audit.issues.length === 0 && (
                        <div className="p-6 text-sm text-muted-foreground" data-testid="issues-empty">
                            No issues found in this crawl — clean audit!
                        </div>
                    )}
                    {audit.issues.map((iss) => {
                        const L = LEVEL[iss.level];
                        const Icon = L.icon;
                        const isOpen = expandedId === iss.id;
                        const hasUrls = (iss.urls || []).length > 0;
                        return (
                            <div key={iss.id} data-testid={`issue-row-${iss.id}`}>
                                <button
                                    type="button"
                                    data-testid={`issue-toggle-${iss.id}`}
                                    onClick={() => setExpandedId(isOpen ? null : iss.id)}
                                    className="w-full p-4 flex items-start gap-4 hover:bg-accent/10 text-left"
                                >
                                    <div className={`w-8 h-8 border ${L.color} grid place-items-center shrink-0`}>
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-sm font-medium flex items-center gap-1.5">
                                            {iss.title}
                                            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">{iss.fix}</div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="font-mono text-lg">{iss.count}</div>
                                        <div className="text-xs text-muted-foreground uppercase tracking-wider">{iss.severity}</div>
                                    </div>
                                </button>
                                {isOpen && (
                                    <div className="px-4 pb-4 pl-16 space-y-3" data-testid={`issue-detail-${iss.id}`}>
                                        {iss.why && (
                                            <div>
                                                <div className="text-[10px] uppercase tracking-widest text-primary mb-1">Why it matters</div>
                                                <p className="text-xs text-muted-foreground leading-relaxed">{iss.why}</p>
                                            </div>
                                        )}
                                        <div>
                                            <div className="text-[10px] uppercase tracking-widest text-primary mb-1">
                                                Affected URLs {hasUrls ? `(${iss.urls.length}${iss.count > iss.urls.length ? ` of ${iss.count}` : ""})` : ""}
                                            </div>
                                            {hasUrls ? (
                                                <ul className="space-y-1 max-h-56 overflow-y-auto" data-testid={`issue-urls-${iss.id}`}>
                                                    {iss.urls.map((u) => (
                                                        <li key={u}>
                                                            <a href={u} target="_blank" rel="noopener noreferrer"
                                                               className="text-xs font-mono text-foreground/80 hover:text-primary inline-flex items-center gap-1 break-all">
                                                                <ExternalLink className="w-3 h-3 shrink-0" /> {u}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="text-xs text-muted-foreground" data-testid={`issue-urls-empty-${iss.id}`}>
                                                    Site-wide finding — no single-URL breakdown available for this check.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
